/**
 * Player del module kind `live_session` (F3-G — UPGRADE del stub thin de F3-A).
 * Enlaza la `class_session` (por `course_module.ref_id`) sincronizada y muestra:
 * la próxima ocurrencia (fecha/hora), el botón "Entrar" cuando está EN VIVO
 * (navega a la pantalla de acceso), la cuenta atrás si aún no abre, y "Ver
 * grabación" cuando existe (re-ver con el player estándar).
 */
import { type Component, createMemo, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { useNavigate, useParams } from "@tanstack/solid-router";
import { Radio, Video } from "lucide-solid";
import { formatCountdown, joinWindowState } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { EnVivoBadge } from "@/features/live/components/en-vivo-badge";
import { RecordingPlayer } from "@/features/live/components/recording-player";
import { useNow } from "@/features/live/hooks/use-now";
import { useLiveSession } from "@/features/live/hooks/use-live-data";
import type { ModuleComponentProps } from "./registry";

/** `HH:MM` a partir de un `HH:MM:SS` (o vacío). */
const formatHm = (v: string | null | undefined) => (v ?? "").slice(0, 5);

/** Visor de consumo de una clase en vivo (enlazada por refId → class_session). */
export const LiveSessionModuleViewer: Component<ModuleComponentProps> = (props) => {
  const { t } = useI18n();
  const now = useNow();
  const navigate = useNavigate();
  const params = useParams({ from: "/u/$u" });

  const sessionQuery = useLiveSession(() => props.refId ?? undefined);
  const row = () => sessionQuery.data?.[0];
  const times = () => ({
    date: row()?.date,
    startTime: row()?.start_time,
    endTime: row()?.end_time,
  });
  const windowState = createMemo(() => joinWindowState(now(), times()));

  const goLive = () => {
    const id = props.refId;
    if (id) {
      void navigate({ to: "/u/$u/live/$sessionId", params: { u: params().u, sessionId: id } });
    }
  };

  return (
    <div
      class="flex flex-col gap-2 rounded-lg border-2 border-border bg-muted/30 p-4"
      data-testid="live-session-module"
    >
      <div class="flex items-center gap-2 text-sm font-semibold">
        <Radio class="h-4 w-4 text-primary" />
        {t(msg`Live class`)}
        <EnVivoBadge times={times()} />
      </div>

      <Show
        when={props.refId && row()}
        fallback={
          <p class="text-xs text-muted-foreground">
            {t(msg`This module is not linked to a class session yet.`)}
          </p>
        }
      >
        <p class="text-xs text-muted-foreground" data-testid="live-module-when">
          {row()?.topic ? `${row()?.topic} · ` : ""}
          {row()?.date} · {formatHm(row()?.start_time)}–{formatHm(row()?.end_time)}
        </p>

        <Show when={windowState().phase === "before"}>
          <p class="text-xs text-muted-foreground" data-testid="live-module-countdown">
            {(() => {
              const countdown = formatCountdown(windowState().msUntilOpen);
              return t(msg`Opens in ${countdown}`);
            })()}
          </p>
        </Show>

        <div class="mt-1 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={!windowState().isLiveNow}
            onClick={goLive}
            class="inline-flex w-fit items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="live-session-join"
          >
            <Video class="h-3.5 w-3.5" />
            <Show when={windowState().isLiveNow} fallback={t(msg`Not live yet`)}>
              {t(msg`Enter`)}
            </Show>
          </button>

          <Show when={row()?.recording_video_id}>
            <RecordingPlayer
              videoId={row()!.recording_video_id!}
              title={row()?.topic ?? undefined}
            />
          </Show>
        </div>
      </Show>
    </div>
  );
};
